<template>
  <div id="app-root">
    <router-view />
    <CustomFoodMigrationPrompt />
    <SchemaUpgradeOverlay />

    <!-- Update available toast -->
    <Transition v-if="!isNativeAndroid" name="update-toast">
      <div v-if="needRefresh" class="update-toast" role="alert">
        <span class="update-toast__message">A new version is available.</span>
        <div class="update-toast__actions">
          <button class="update-toast__btn update-toast__btn--primary" @click="acceptUpdate">
            Update now
          </button>
          <button class="update-toast__btn update-toast__btn--ghost" @click="dismissUpdate">
            Later
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { watch } from 'vue';
import { useRegisterSW } from 'virtual:pwa-register/vue';
import { Capacitor } from '@capacitor/core';
import CustomFoodMigrationPrompt from '@/components/CustomFoodMigrationPrompt.vue';
import SchemaUpgradeOverlay from '@/components/SchemaUpgradeOverlay.vue';

const isNativeAndroid = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';

// PWA update handling
const { needRefresh, updateServiceWorker } = useRegisterSW();

// On native Android, apply updates automatically without showing the toast
if (isNativeAndroid) {
  watch(needRefresh, (val) => {
    if (val) void updateServiceWorker(true);
  });
}

const acceptUpdate = (): void => {
  void updateServiceWorker(true);
};

const dismissUpdate = (): void => {
  needRefresh.value = false;
};
</script>

<style scoped>
#app-root {
  min-height: 100vh;
  width: 100%;
}

.update-toast {
  position: fixed;
  bottom: max(calc(var(--tab-bar-height, 56px) + var(--space-md)), env(safe-area-inset-bottom, 0px) + calc(var(--tab-bar-height, 56px) + var(--space-md)));
  left: 50%;
  transform: translateX(-50%);
  z-index: 900;
  display: flex;
  align-items: center;
  gap: var(--space-md);
  background: var(--color-background-secondary, #fff);
  border: 1px solid var(--color-border-medium, #ddd);
  border-radius: var(--radius-lg, 12px);
  padding: var(--space-sm) var(--space-md);
  box-shadow: var(--shadow-lg);
  white-space: nowrap;
  max-width: calc(100vw - 2 * var(--space-md));
}

.update-toast__message {
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
  flex: 1;
}

.update-toast__actions {
  display: flex;
  gap: var(--space-xs);
  flex-shrink: 0;
}

.update-toast__btn {
  border: none;
  border-radius: var(--radius-sm, 6px);
  padding: 6px 12px;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: opacity 150ms ease;
}

.update-toast__btn:active {
  opacity: 0.75;
}

.update-toast__btn--primary {
  background: var(--color-primary);
  color: #fff;
}

.update-toast__btn--ghost {
  background: transparent;
  color: var(--color-text-secondary);
}

.update-toast-enter-active,
.update-toast-leave-active {
  transition: opacity 200ms ease, transform 200ms ease;
}

.update-toast-enter-from,
.update-toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(12px);
}
</style>
